// /mensajes-kine — inbox del kine con todas las conversaciones activas
// (pacientes con al menos 1 mensaje). Click navega a /paciente/:id?tab=mensajes
// — el thread real vive en DetallePaciente tab Mensajes (decisión Q7).
//
// Sprint 3 B4: wire reemplaza mock previo. Sidebar inline reemplazado por
// <KineSidebar /> + <KineHeader /> shared (cierra deuda preexistente sobre
// duplicación del sidebar en MensajesKine).

import { useMemo, useState } from 'react';
import { Search, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Avatar } from '../components/Avatar';
import { KineHeader } from '../components/kine/KineHeader';
import { KineSidebar } from '../components/kine/KineSidebar';
import { formatRelativeDate } from '../../lib/utils';
import {
  useConversacionesKine,
  type ConversacionInbox,
} from '@/lib/supabase/queries/useConversacionesKine';
import { useKineProfile } from '@/lib/supabase/queries/useKineProfile';

export function MensajesKine() {
  const navigate = useNavigate();
  const { data: conversaciones, error, loading } = useConversacionesKine();
  const { data: kineProfile } = useKineProfile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!conversaciones) return null;
    const q = search.trim().toLowerCase();
    if (!q) return conversaciones;
    return conversaciones.filter((c) =>
      `${c.pacienteNombre} ${c.pacienteApellido}`.toLowerCase().includes(q),
    );
  }, [conversaciones, search]);

  const totalUnread =
    conversaciones?.reduce((acc, c) => acc + c.unreadCount, 0) ?? 0;

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={kineProfile?.nombre ?? undefined}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="mensajes"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-4 md:p-6 min-w-0">
          <div className="max-w-[1000px]">
            <h1 className="text-3xl mb-1">Mensajes</h1>
            <p className="text-muted-foreground text-sm mb-6">
              {loading
                ? 'Cargando…'
                : totalUnread > 0
                  ? `${totalUnread} mensaje${totalUnread === 1 ? '' : 's'} sin leer`
                  : 'Todo al día'}
            </p>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 relative">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar conversación..."
                  className="w-full pl-11 pr-4 py-2.5 text-sm bg-input-background border border-border rounded-full transition-all duration-200 ease-out focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            </div>

            {loading && <InboxSkeleton />}
            {error && !loading && <InboxError message={error} />}
            {!loading && !error && filtered && filtered.length === 0 && (
              <InboxEmpty isFiltered={search.trim().length > 0} />
            )}
            {!loading && !error && filtered && filtered.length > 0 && (
              <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                {filtered.map((c, idx) => (
                  <ConversacionRow
                    key={c.pacienteId}
                    conv={c}
                    colorIndex={idx}
                    isLast={idx === filtered.length - 1}
                    onClick={() =>
                      navigate(`/paciente/${c.pacienteId}?tab=mensajes`)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────

function ConversacionRow({
  conv,
  colorIndex,
  isLast,
  onClick,
}: {
  conv: ConversacionInbox;
  colorIndex: number;
  isLast: boolean;
  onClick: () => void;
}) {
  const fullName = `${conv.pacienteNombre} ${conv.pacienteApellido}`;
  const timeLabel = conv.lastMessageAt
    ? formatRelativeDate(new Date(conv.lastMessageAt))
    : '';
  // Preview con prefijo si el último fue del kine (señala "respondiste").
  const previewPrefix =
    conv.lastMessageSender === 'kine' ? 'Vos: ' : '';
  const preview = conv.lastMessageContent
    ? `${previewPrefix}${conv.lastMessageContent}`
    : 'Sin mensajes';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full p-4 md:p-5 flex items-center gap-4 hover:bg-muted/30 transition-all duration-200 ease-out text-left ${
        !isLast ? 'border-b border-border' : ''
      }`}
    >
      <Avatar name={fullName} colorIndex={colorIndex} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5 gap-2">
          <h3
            className={`truncate ${
              conv.unreadCount > 0 ? 'font-semibold' : 'font-medium'
            }`}
          >
            {fullName}
          </h3>
          <span className="text-xs text-muted-foreground shrink-0">
            {timeLabel}
          </span>
        </div>
        <p
          className={`text-sm truncate ${
            conv.unreadCount > 0
              ? 'text-foreground'
              : 'text-muted-foreground'
          }`}
        >
          {preview}
        </p>
      </div>
      {conv.unreadCount > 0 && (
        <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold shrink-0 tabular-nums">
          {conv.unreadCount}
        </div>
      )}
    </button>
  );
}

function InboxSkeleton() {
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`p-5 flex items-center gap-4 animate-pulse ${
            i < 2 ? 'border-b border-border' : ''
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-40 bg-muted rounded" />
            <div className="h-3 w-60 bg-muted rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function InboxError({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-8 text-center">
      <p className="font-semibold text-orange-600 mb-1">
        No pudimos cargar tus conversaciones
      </p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function InboxEmpty({ isFiltered }: { isFiltered: boolean }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-8 text-center">
      <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mx-auto mb-3">
        <MessageCircle
          className="w-6 h-6 text-primary"
          strokeWidth={1.5}
        />
      </div>
      <p className="font-semibold mb-1">
        {isFiltered
          ? 'No hay conversaciones que coincidan'
          : 'Sin conversaciones todavía'}
      </p>
      <p className="text-sm text-muted-foreground">
        {isFiltered
          ? 'Probá con otro término de búsqueda.'
          : 'Las conversaciones con tus pacientes van a aparecer acá.'}
      </p>
    </div>
  );
}
